// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

/**
 * F12.A: "Confirm abandonment" page — step 2 of the lost-passcode-AND-
 * lost-seed recovery flow. Linked from the magic-link email as
 * `/abandon?token=<raw>`. User must retype their email and tick the
 * "I understand this is irreversible" checkbox before `POST
 * /auth/abandon-confirm` fires.
 *
 * No per-vault preview (per F12.A design Q3): the page is text-only so
 * an attacker who gets hold of the URL can't enumerate what vaults the
 * account has.
 */

import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../api.js';
import '../styles/account-abandon.css';

export function AbandonConfirm() {
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [understand, setUnderstand] = useState(false);
  const [status, setStatus] = useState<
    'ready' | 'confirming' | 'done' | 'already' | 'error'
  >('ready');
  const [error, setError] = useState<string | null>(null);
  const [vaultCount, setVaultCount] = useState<number | null>(null);

  useEffect(() => {
    // preact-iso doesn't give us a query-param helper; parse window.location
    // directly. The token is the only expected param.
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('token');
    if (raw) setToken(raw);
  }, []);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (!token || !email.trim() || !understand) return;
    setStatus('confirming');
    setError(null);
    try {
      const resp = await api.abandonConfirm({
        token,
        email_confirmation: email.trim(),
      });
      setVaultCount(resp.vault_count);
      setStatus(resp.status === 'already_abandoned' ? 'already' : 'done');
    } catch (err) {
      if (err instanceof ApiError) {
        // 400 covers all "try again" cases: link invalid / expired /
        // malformed / email-mismatch. The backend deliberately uses
        // one status here because CloudFront rewrites 404 → SPA HTML
        // at the edge (see handler comment in lib.rs abandon_confirm).
        if (err.status === 400) {
          setError(
            'This link is not valid, expired, or the email you entered does not match. Request a new link and try again.',
          );
        } else if (err.status === 409) {
          setError(
            'This link has already been used. Your account is already abandoned.',
          );
        } else if (err.status === 429) {
          setError('Too many attempts. Please wait a minute and try again.');
        } else {
          setError('Something went wrong. Please try again in a moment.');
        }
      } else {
        setError('Something went wrong. Please try again in a moment.');
      }
      setStatus('error');
    }
  };

  if (!token) {
    return (
      <div class="abandon-page">
        <h1 class="abandon-title">Missing link</h1>
        <p>
          This page needs a token from the magic-link email. If you're trying
          to abandon your account, start from{' '}
          <a href="/account/abandon">the recovery request page</a> instead.
        </p>
      </div>
    );
  }

  if (status === 'done' || status === 'already') {
    return (
      <div class="abandon-page">
        <h1 class="abandon-title">
          {status === 'already'
            ? 'Account already abandoned'
            : 'Account abandoned'}
        </h1>
        <p>
          Your Cobault account has been marked abandoned. The Bitcoin held by{' '}
          {vaultCount !== null ? `your ${vaultCount} vault${vaultCount === 1 ? '' : 's'}` : 'your vaults'}{' '}
          will be returned to the on-chain payout address committed when each
          vault was created.
        </p>
        <ul class="abandon-bullets">
          <li>
            Vaults with no active trade refund within hours of the next watcher
            cycle.
          </li>
          <li>
            Vaults with an active trade settle on their contract's expiry date.
          </li>
        </ul>
        <p>
          You have been signed out. You can close this tab.
        </p>
      </div>
    );
  }

  return (
    <div class="abandon-page">
      <h1 class="abandon-title">Confirm account abandonment</h1>

      <div class="abandon-warning">
        <p>
          <strong>This is irreversible.</strong> Once confirmed, you cannot
          sign in to this account again, and your vault funds flow back to
          your on-chain payout addresses on the schedules described below.
        </p>
      </div>

      <h2 class="abandon-subtitle">What happens next</h2>
      <p>
        After you confirm:
      </p>
      <ul class="abandon-bullets">
        <li>
          Vaults that never had an active trade are refunded to your payout
          address within hours.
        </li>
        <li>
          Vaults with an active trade settle on their contract's expiry date
          (usually within days); settlement proceeds flow to your payout
          address automatically.
        </li>
        <li>
          Your session is ended immediately. All sign-in tokens are revoked.
        </li>
        <li>
          If you later want to use Cobault again, you'll register a fresh
          account — you can re-use the same email.
        </li>
      </ul>

      <form class="abandon-form" onSubmit={onSubmit}>
        <label class="abandon-label" for="abandon-email-confirm">
          Retype the email address of the account to abandon
        </label>
        <input
          id="abandon-email-confirm"
          type="email"
          class="abandon-input"
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          autocomplete="email"
          autoFocus
          required
          disabled={status === 'confirming'}
        />

        <label class="abandon-checkbox-row">
          <input
            type="checkbox"
            checked={understand}
            onChange={(e) => setUnderstand((e.target as HTMLInputElement).checked)}
            disabled={status === 'confirming'}
          />
          <span>
            I understand this is irreversible and that funds will flow to the
            on-chain payout addresses committed at vault creation.
          </span>
        </label>

        {error && <p class="abandon-error">{error}</p>}

        <div class="abandon-actions">
          <a class="abandon-cancel" href="/">
            Cancel
          </a>
          <button
            type="submit"
            class="abandon-submit abandon-submit-danger"
            disabled={
              status === 'confirming' || !email.trim() || !understand
            }
          >
            {status === 'confirming' ? 'Confirming...' : 'Abandon my account'}
          </button>
        </div>
      </form>
    </div>
  );
}
